<template>
  <div class="kingdom-panel">
    <div class="kingdom-body">
      <div class="kingdom-main">
        <div ref="viewEl" class="kingdom-view">
          <div v-if="tabs.length" class="kingdom-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.code"
              type="button"
              :class="{ 'is-on': king && tab.code === king.code }"
              @click="$emit('switch-kingdom', tab.code)"
            >{{ tab.title }}</button>
          </div>
          <div ref="shiftEl" class="kingdom-shift" :style="{ width: chartWidth }">
            <div ref="chartEl" class="kingdom-chart" />
            <div
              v-if="pickedDay >= 0"
              class="kingdom-follow"
              :style="{ left: dateLeft(pickedDay) }"
            >{{ catchupCount(pickedDay) }}个补涨爆量</div>
            <div class="kingdom-dates">
              <span
                v-for="(date, index) in dates"
                :key="date + index"
                class="kingdom-dates__item"
                :style="{ left: dateLeft(index) }"
                @click="pickDay(index)"
              >
                <b>{{ shortDate(date) }}</b>
                <i :class="nodeClass(date)">{{ nodeText(date) }}</i>
                <i :class="soilClass(date)">{{ soilText(date) }}</i>
                <i v-if="catchupOn(date)" class="is-catchup">补涨爆量</i>
              </span>
            </div>
          </div>
        </div>
        <div ref="barEl" class="kingdom-bar" @scroll="onBarScroll">
          <div :style="{ width: chartWidth }" />
        </div>
      </div>
      <aside class="kingdom-roster" :class="{ 'is-adding': addOpen }">
        <div class="kingdom-addbar">
          <button type="button" class="kingdom-roster__save" @click="toggleAdd">{{ addOpen ? '收起' : '新增' }}</button>
        </div>
        <div v-if="addOpen" class="kingdom-add">
          <div class="kingdom-add__input">
            <input v-model="addQuery" type="text" placeholder="代码或名称" @keyup.enter="submitTyped">
            <button type="button" :disabled="addSearching" @click="submitTyped">新增</button>
          </div>
          <button
            v-for="row in searchHits"
            :key="'hit-' + row.code"
            type="button"
            class="kingdom-add__row"
            :class="{ 'is-on': onChart(row.code) }"
            :disabled="onChart(row.code)"
            @click="submitOne(row)"
          >{{ row.name }} {{ row.code }}</button>
          <div class="kingdom-add__tabs">
            <button type="button" :class="{ on: addTab === 'veteran' }" @click="addTab = 'veteran'">老臣池</button>
            <button type="button" :class="{ on: addTab === 'height' }" @click="addTab = 'height'">非龙头最高高度</button>
          </div>
          <div class="kingdom-add__list">
            <em v-if="addLoading">读取中</em>
            <em v-else-if="!addRows.length">{{ addEmpty }}</em>
            <button
              v-for="row in addRows"
              :key="addTab + row.code"
              type="button"
              class="kingdom-add__row"
              :class="{ 'is-on': onChart(row.code), 'is-picked': picked[row.code] && !onChart(row.code) }"
              :disabled="onChart(row.code)"
              @click="togglePick(row)"
            >{{ row.name }}<i v-if="onChart(row.code)">已在图上</i><i v-else-if="picked[row.code]">待加入</i></button>
          </div>
        </div>
        <em v-if="loading">正在整理同概念3连板</em>
        <em v-else-if="note">{{ note }}</em>
        <button type="button" class="kingdom-roster__save" :disabled="saving" title="把待加入的股票画上图，并按今天留下" @click="emitSave">保存</button>
        <div v-if="king && !dismissed[king.code]" class="kingdom-roster__row">
          <i :style="{ background: king.color }" />
          <span>{{ king.name }}</span>
          <button type="button" @click="dismiss(king.code)">×</button>
        </div>
        <div v-for="peer in peers" v-show="!dismissed[peer.code]" :key="peer.code" class="kingdom-roster__row">
          <i :style="{ background: peer.color }" />
          <span>{{ peer.name }}</span>
          <button type="button" @click="dismiss(peer.code)">×</button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import stockApi from '@/api/stockApi'

function normCode(value) {
  const digits = String(value || '').split('.')[0].replace(/\D/g, '')
  if (digits.length < 6) return ''
  return digits.slice(-6)
}

export default {
  name: 'KingdomPanel',
  props: {
    dates: { type: Array, default: () => [] },
    king: { type: Object, default: null },
    peers: { type: Array, default: () => [] },
    marks: { type: Array, default: () => [] },
    catchups: { type: Array, default: () => [] },
    dayMarks: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    note: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    tradeDate: { type: String, default: '' },
    tabs: { type: Array, default: () => [] },
    concepts: { type: Array, default: () => [] }
  },
  data() {
    return {
      chart: null,
      dismissed: {},
      pickedDay: -1,
      addOpen: false,
      addQuery: '',
      addTab: 'veteran',
      addLoading: false,
      addSearching: false,
      veteranRows: [],
      heightRows: [],
      searchHits: [],
      picked: {},
      labelFront: ''
    }
  },
  computed: {
    chartPixelWidth() {
      const count = (this.dates || []).length || 1
      return 52 + Math.max(count - 1, 1) * 88 + 102
    },
    chartWidth() {
      return `${this.chartPixelWidth}px`
    },
    addRows() {
      return this.addTab === 'height' ? this.heightRows : this.veteranRows
    },
    addEmpty() {
      if (this.addTab === 'height') return '这个国王还没有非龙头最高高度名单'
      if (!(this.concepts || []).length) return '先选定主概念'
      return '老臣池暂无'
    }
  },
  watch: {
    dates() {
      this.pickedDay = -1
      this.draw()
    },
    king: { deep: true, handler() { this.draw() } },
    'king.code'() {
      if (this.addOpen) this.loadPools()
    },
    concepts() {
      if (this.addOpen) this.loadPools()
    },
    peers: { deep: true, handler() { this.draw() } },
    marks: { deep: true, handler() { this.draw() } },
    catchups: { deep: true, handler() { this.draw() } },
    chartWidth() { this.$nextTick(() => this.draw()) }
  },
  mounted() {
    this._onResize = () => { this.draw() }
    window.addEventListener('resize', this._onResize)
    this.draw()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
    if (this.chart) this.chart.dispose()
    this.chart = null
  },
  methods: {
    premiumText(peer, item) {
      const short = String((peer && peer.name) || '').slice(0, 2)
      const height = item && item.height > 0 ? `${item.height}B ` : ''
      const open = item && Number.isFinite(item.openPct) ? `${item.openPct.toFixed(2)}%` : '--'
      const close = item && Number.isFinite(item.closePct) ? `${item.closePct.toFixed(2)}%` : '--'
      return `${short} ${height}${open}//${close}`
    },
    nodeText(date) {
      const mark = (this.dayMarks && this.dayMarks[date]) || {}
      const score = Number(mark.nodeScore)
      if (!Number.isFinite(score)) return '—'
      return `${Math.round(score)}${mark.nodeHit ? '★' : ''}`
    },
    nodeClass(date) {
      const mark = (this.dayMarks && this.dayMarks[date]) || {}
      return Number(mark.nodeScore) >= 7 ? 'is-hit' : ''
    },
    soilText(date) {
      const soil = ((this.dayMarks && this.dayMarks[date]) || {}).soil
      if (soil === 'S_PLUS') return 'S+'
      if (soil === 'S') return 'S'
      return '无'
    },
    soilClass(date) {
      const soil = ((this.dayMarks && this.dayMarks[date]) || {}).soil
      if (soil === 'S_PLUS') return 'is-plus'
      if (soil === 'S') return 'is-soil'
      return 'is-none'
    },
    catchupOn(date) {
      const list = this.catchups || []
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].date === date && !this.dismissed[list[i].code]) return true
      }
      return false
    },
    catchupCount(index) {
      const date = (this.dates || [])[index]
      if (!date) return 0
      const list = this.catchups || []
      let count = 0
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].date === date && !this.dismissed[list[i].code]) count += 1
      }
      return count
    },
    pickDay(index) {
      const count = (this.dates || []).length
      if (!Number.isFinite(index) || index < 0 || index >= count) {
        this.pickedDay = -1
      } else {
        this.pickedDay = index
      }
      this.draw()
    },
    bindChartClick() {
      if (!this.chart || this._chartClick) return
      this._chartClick = true
      this.chart.on('click', (params) => {
        const code = String((params && params.seriesId) || '').split('|')[0]
        if (!/^\d{6}$/.test(code)) return
        this.labelFront = code
        this.draw()
      })
      this.chart.getZr().on('click', (event) => {
        if (!this.chart) return
        const point = [event.offsetX, event.offsetY]
        if (!this.chart.containPixel({ gridIndex: 0 }, point)) return
        const raw = this.chart.convertFromPixel({ gridIndex: 0 }, point)
        const index = Array.isArray(raw) ? Math.round(raw[0]) : -1
        this.pickDay(index)
      })
    },
    dismiss(code) {
      this.$set(this.dismissed, code, true)
      this.draw()
    },
    emitSave() {
      const picked = []
      const pickedSet = {}
      const source = (this.addRows || []).concat(this.searchHits || [])
      for (let i = 0; i < source.length; i += 1) {
        const row = source[i] || {}
        const code = normCode(row.code)
        if (!code || !this.picked[code] || pickedSet[code] || this.onChart(code)) continue
        pickedSet[code] = true
        picked.push({ code, name: row.name || code })
        if (this.dismissed[code]) this.$delete(this.dismissed, code)
      }
      const kept = []
      const removed = []
      ;(this.peers || []).forEach((peer) => {
        const code = normCode(peer.code)
        if (!code) return
        if (this.dismissed[code] && !pickedSet[code]) removed.push(code)
        else kept.push({ code, name: peer.name || code })
      })
      for (let i = 0; i < picked.length; i += 1) {
        if (!kept.some((item) => item.code === picked[i].code)) kept.push(picked[i])
      }
      this.picked = {}
      this.$emit('save-hidden', { kept, removed, picked })
    },
    onChart(code) {
      const short = normCode(code)
      if (!short) return false
      if (this.dismissed[short]) return false
      if (this.king && normCode(this.king.code) === short) return true
      const peers = this.peers || []
      for (let i = 0; i < peers.length; i += 1) {
        if (normCode(peers[i].code) === short) return true
      }
      return false
    },
    toggleAdd() {
      this.addOpen = !this.addOpen
      if (this.addOpen) this.loadPools()
    },
    togglePick(row) {
      const code = normCode(row && row.code)
      if (!code || this.onChart(code)) return
      if (this.picked[code]) this.$delete(this.picked, code)
      else this.$set(this.picked, code, true)
    },
    placeRows(rows) {
      const fresh = []
      for (let i = 0; i < (rows || []).length; i += 1) {
        const row = rows[i] || {}
        const code = normCode(row.code)
        if (!code || this.onChart(code)) continue
        const peers = this.peers || []
        let hidden = false
        for (let j = 0; j < peers.length; j += 1) {
          if (normCode(peers[j].code) === code && this.dismissed[code]) hidden = true
        }
        if (hidden) {
          this.$delete(this.dismissed, code)
          continue
        }
        fresh.push({ code, name: row.name || code })
      }
      if (fresh.length) this.$emit('add-peers', fresh)
      this.draw()
    },
    submitOne(row) {
      this.placeRows([row])
      this.searchHits = []
      this.addQuery = ''
    },
    async submitTyped() {
      const query = String(this.addQuery || '').trim()
      if (!query || this.addSearching) return
      this.addSearching = true
      try {
        const res = await stockApi.searchStocks(query)
        const data = (res && res.data) || []
        const rows = []
        const seen = {}
        for (let i = 0; i < data.length; i += 1) {
          const code = normCode(data[i] && data[i].code)
          const name = String((data[i] && data[i].name) || '').trim()
          if (!code || seen[code]) continue
          seen[code] = true
          rows.push({ code, name: name || code })
        }
        if (!rows.length) {
          this.searchHits = []
          this.$message.warning('没有这只股票')
          return
        }
        const exact = rows.find((row) => row.code === query || row.name === query)
        const chosen = exact || (rows.length === 1 ? rows[0] : null)
        if (!chosen) {
          this.searchHits = rows.slice(0, 8)
          return
        }
        if (this.onChart(chosen.code)) {
          this.$message.warning('已经在图上')
          return
        }
        this.submitOne(chosen)
      } catch (err) {
        this.$message.error((err && err.message) || '搜索失败')
      } finally {
        this.addSearching = false
      }
    },
    readHeightRows() {
      const code = normCode(this.king && this.king.code)
      if (!code) return []
      try {
        const raw = JSON.parse(localStorage.getItem(`lcc.themeProp.heightManual.v3|${code}`) || '[]')
        const list = Array.isArray(raw) ? raw : []
        const rows = []
        const seen = {}
        for (let i = 0; i < list.length; i += 1) {
          const item = list[i] || {}
          const stockCode = normCode(item.code)
          if (!stockCode || seen[stockCode]) continue
          seen[stockCode] = true
          rows.push({ code: stockCode, name: String(item.name || '').trim() || stockCode })
        }
        return rows
      } catch (err) {
        return []
      }
    },
    async loadPools() {
      this.addLoading = true
      this.heightRows = this.readHeightRows()
      const code = normCode(this.king && this.king.code)
      const names = this.concepts || []
      const tradeDate = String(this.tradeDate || '').slice(0, 10)
      if (!code || !names.length || !tradeDate) {
        this.veteranRows = []
        this.addLoading = false
        return
      }
      const seen = {}
      const rows = []
      try {
        for (let i = 0; i < names.length; i += 1) {
          const res = await stockApi.postLeaderOnce4b({
            tradeDate,
            leaderCode: code,
            concept: names[i],
            conceptSource: 'MERGE'
          })
          const pools = (res && res.pools) || {}
          const blocks = [pools.gt4b, pools.b3, pools.b2]
          for (let b = 0; b < blocks.length; b += 1) {
            const list = (blocks[b] && blocks[b].rows) || []
            for (let r = 0; r < list.length; r += 1) {
              const stockCode = normCode(list[r] && list[r].code)
              if (!stockCode || seen[stockCode] || stockCode === code) continue
              seen[stockCode] = true
              rows.push({ code: stockCode, name: String((list[r] && list[r].name) || '').trim() || stockCode })
            }
          }
        }
      } catch (err) {
        this.$message.error((err && err.message) || '老臣池读取失败')
      }
      this.veteranRows = rows
      this.addLoading = false
    },
    catchupPoint(x, y, offset) {
      return {
        coord: [x, y],
        symbol: 'circle',
        symbolSize: 18,
        symbolOffset: offset || [0, 0],
        itemStyle: { color: '#7c3aed', borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        tip: '补涨爆量'
      }
    },
    shortDate(date) {
      const text = String(date || '')
      return text.length >= 10 ? text.slice(5) : text
    },
    axisSpan() {
      const count = (this.dates || []).length || 1
      return { min: 0, max: Math.max(count - 1, 1) }
    },
    dateLeft(index) {
      const width = this.chartPixelWidth
      const plot = Math.max(width - 52 - 78, 1)
      const span = this.axisSpan()
      const x = 52 + ((index - span.min) / (span.max - span.min)) * plot
      return `${x}px`
    },
    onBarScroll(event) {
      const shift = this.$refs.shiftEl
      if (shift) shift.style.left = `${-event.target.scrollLeft}px`
    },
    percentExtent(values) {
      let dataMin = Infinity
      let dataMax = -Infinity
      ;(values || []).forEach((value) => {
        const n = Number(value)
        if (!Number.isFinite(n)) return
        if (n < dataMin) dataMin = n
        if (n > dataMax) dataMax = n
      })
      const step = 10
      if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return { min: 10, max: 20 }
      const axisMin = Math.max(10, Math.floor(dataMin / step) * step)
      const rawMax = Math.ceil((dataMax - 1e-9) / step) * step
      return { min: axisMin, max: rawMax <= axisMin ? axisMin + step : rawMax }
    },
    burstPoint(mark, y, kingName, x, offset) {
      const point = {
        coord: [x, y],
        symbol: 'circle',
        symbolSize: 18,
        itemStyle: { color: mark.color, borderColor: '#fff', borderWidth: 2 },
        tip: `${kingName}<br/>${mark.date} · ${mark.name}`,
        label: { show: false }
      }
      if (offset) point.symbolOffset = offset
      return point
    },
    draw() {
      this.$nextTick(() => {
        const el = this.$refs.chartEl
        if (!el) return
        const view = this.$refs.viewEl
        const height = view && view.clientHeight > 308 ? view.clientHeight - 68 : 620
        el.style.height = `${height}px`
        el.style.width = this.chartWidth
        if (!this.chart) this.chart = echarts.init(el)
        this.bindChartClick()
        const dates = this.dates || []
        const king = this.king
        const showKing = !!(king && king.code && !this.dismissed[king.code])
        const values = showKing && king.values ? king.values : []
        const bag = values.slice()
        const peers = (this.peers || []).filter((peer) => !this.dismissed[peer.code])
        const catchups = (this.catchups || []).filter((item) => !this.dismissed[item.code])
        for (let i = 0; i < peers.length; i += 1) {
          const returns = peers[i].returns || []
          const tails = peers[i].tails || []
          for (let j = 0; j < returns.length; j += 1) bag.push(returns[j])
          for (let j = 0; j < tails.length; j += 1) bag.push(tails[j])
        }
        const extent = this.percentExtent(bag)
        const marks = showKing ? (this.marks || []) : []
        const catchupDates = {}
        catchups.forEach((item) => { catchupDates[item.date] = true })
        const kingBottomDates = {}
        marks.forEach((mark) => { kingBottomDates[mark.date] = true })
        const kingName = king && king.name ? king.name : ''
        const dateIndex = {}
        for (let i = 0; i < dates.length; i += 1) dateIndex[dates[i]] = i
        const pointOffsets = peers.map((peer) => {
          const width = Math.max((peer.returns || []).length, (peer.tails || []).length, dates.length)
          const row = []
          for (let j = 0; j < width; j += 1) row.push(null)
          return row
        })
        const runs = {}
        for (let i = 0; i < peers.length; i += 1) {
          const returns = peers[i].returns || []
          let j = 0
          while (j < returns.length) {
            if (!Number.isFinite(returns[j])) {
              j += 1
              continue
            }
            const from = j
            while (j < returns.length && Number.isFinite(returns[j])) j += 1
            if (!runs[from]) runs[from] = []
            runs[from].push({ index: i, from, to: j })
          }
        }
        const runKeys = Object.keys(runs)
        for (let g = 0; g < runKeys.length; g += 1) {
          const members = runs[runKeys[g]]
          for (let lane = 0; lane < members.length; lane += 1) {
            let offset = 0
            if (members.length === 2) offset = lane === 0 ? -0.18 : 0.18
            else if (members.length > 2) offset = [-0.18, 0, 0.18][lane % 3]
            const member = members[lane]
            for (let j = member.from; j < member.to; j += 1) pointOffsets[member.index][j] = offset
          }
        }
        const offsetAt = (index, at) => {
          const row = pointOffsets[index]
          if (!row) return 0
          if (row[at] != null) return row[at]
          for (let k = at; k >= 0; k -= 1) {
            if (row[k] != null) return row[k]
          }
          return 0
        }
        const kingPoints = []
        for (let i = 0; i < values.length; i += 1) {
          const raw = values[i]
          const n = Number(raw)
          kingPoints.push(raw != null && raw !== '' && Number.isFinite(n) ? [i, n] : null)
        }
        const kingSeries = {
          id: (king && king.code) || 'king',
          name: kingName || '国王',
          type: 'line',
          data: kingPoints,
          connectNulls: false,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 8,
          z: 6,
          itemStyle: { color: king && king.color },
          clip: false,
          lineStyle: { color: king && king.color, width: 3 },
          markPoint: {
            symbol: 'circle',
            symbolSize: 18,
            clip: false,
            z: 8,
            data: marks.reduce((list, mark) => {
              const x = dateIndex[mark.date]
              if (x == null) return list
              list.push(this.burstPoint(mark, mark.y, kingName, x))
              if (catchupDates[mark.date]) {
                list.push(this.catchupPoint(x, extent.min, [0, 7]))
                list.push(this.burstPoint(mark, extent.min, kingName, x, [0, -7]))
              } else {
                list.push(this.burstPoint(mark, extent.min, kingName, x))
              }
              return list
            }, []).concat(catchups.reduce((list, item) => {
              const x = dateIndex[item.date]
              if (x == null || kingBottomDates[item.date]) return list
              if (list.some((point) => point.coord && point.coord[0] === x && point.tip === '补涨爆量')) return list
              list.push(this.catchupPoint(x, extent.min))
              return list
            }, []))
          }
        }
        const fades = []
        const lines = peers.map((peer, index) => {
          const returns = peer.returns || []
          const tails = peer.tails || []
          const premiumAt = {}
          ;(peer.premiums || []).forEach((item) => { premiumAt[item.index] = item })
          const points = []
          const boards = peer.boards || []
          for (let j = 0; j < returns.length; j += 1) {
            const covered = Number.isFinite(returns[j]) && Number.isFinite(tails[j]) && tails[j] !== returns[j]
            points.push(!covered && Number.isFinite(returns[j]) ? [j + offsetAt(index, j), returns[j]] : null)
          }
          let tailPoints = 0
          const fade = []
          const deepSegments = []
          const resumeSegments = []
          const labels = []
          const ink = peer.color || '#303133'
          let prevSpot = null
          for (let j = 0; j < tails.length; j += 1) {
            if (!Number.isFinite(tails[j])) {
              fade.push(null)
              prevSpot = null
              continue
            }
            tailPoints += 1
            const spot = [j + offsetAt(index, j), tails[j]]
            const premium = premiumAt[j]
            const boardToday = Number(boards[j]) > 0
            const nextBoard = j + 1 < tails.length && Number.isFinite(tails[j + 1]) && Number(boards[j + 1]) > 0
            if (boardToday && prevSpot) {
              resumeSegments.push([prevSpot, spot])
              fade.push(null)
              if (!nextBoard) fade.push(spot)
            } else {
              fade.push(spot)
            }
            prevSpot = spot
            if (premium) {
              labels.push({
                value: spot,
                labelText: this.premiumText(peer, premium)
              })
            }
            if (!premium || premium.closePct > -5 || j < 1) continue
            const prev = Number.isFinite(tails[j - 1]) ? tails[j - 1] : returns[j - 1]
            if (!Number.isFinite(prev)) continue
            deepSegments.push([
              [j - 1 + offsetAt(index, j - 1), prev],
              spot
            ])
          }
          if (tailPoints > 0) {
            fades.push({
              id: `${peer.code}|fade`,
              name: peer.name,
              type: 'line',
              data: fade,
              connectNulls: false,
              showSymbol: true,
              symbol: 'circle',
              symbolSize: 14,
              z: 4,
              clip: false,
              label: {
                show: false,
                formatter: (params) => (params.data && params.data.labelText) || ''
              },
              labelLayout: { hideOverlap: false },
              itemStyle: {
                color: peer.color,
                opacity: 0.3,
                borderColor: '#fff',
                borderWidth: 3
              },
              lineStyle: {
                color: peer.color,
                width: 8,
                opacity: 0.3
              },
              emphasis: { disabled: true }
            })
          }
          if (labels.length) {
            const front = this.labelFront === peer.code
            fades.push({
              id: `${peer.code}|label`,
              name: peer.name,
              type: 'scatter',
              data: labels.map((item) => ({
                value: item.value,
                labelText: item.labelText,
                label: {
                  show: true,
                  position: 'top',
                  distance: 8,
                  color: ink,
                  opacity: 1,
                  fontWeight: 700,
                  fontSize: 12,
                  backgroundColor: '#fff',
                  borderColor: ink,
                  borderWidth: 1,
                  borderRadius: 3,
                  padding: [3, 6]
                }
              })),
              z: front ? 200 : 8,
              zlevel: front ? 2 : 0,
              symbolSize: 8,
              itemStyle: { color: ink, opacity: 1 },
              label: {
                show: true,
                opacity: 1,
                formatter: (params) => (params.data && params.data.labelText) || ''
              },
              emphasis: { disabled: true }
            })
          }
          resumeSegments.forEach((segment) => {
            fades.push({
              id: `${peer.code}|resume|${segment[1][0]}`,
              name: peer.name,
              type: 'line',
              data: segment,
              showSymbol: true,
              symbol: 'circle',
              symbolSize: 14,
              z: 6,
              clip: false,
              label: { show: false },
              itemStyle: {
                color: peer.color,
                borderColor: '#fff',
                borderWidth: 3,
                shadowBlur: 16,
                shadowOffsetY: 6,
                shadowColor: 'rgba(15, 23, 42, 0.62)'
              },
              lineStyle: {
                color: peer.color,
                width: 8,
                shadowBlur: 22,
                shadowOffsetY: 12,
                shadowColor: 'rgba(15, 23, 42, 0.62)'
              },
              emphasis: { disabled: true }
            })
          })
          deepSegments.forEach((segment) => {
            fades.push({
              id: `${peer.code}|deep|${segment[1][0]}`,
              name: peer.name,
              type: 'line',
              data: segment,
              showSymbol: true,
              symbol: 'circle',
              symbolSize: 14,
              z: 7,
              clip: false,
              label: { show: false },
              itemStyle: { color: '#14532d', opacity: 1, borderWidth: 0 },
              lineStyle: { color: '#14532d', width: 8, opacity: 1 },
              emphasis: { disabled: true }
            })
          })
          return {
            id: `${peer.code}|line`,
            name: peer.name,
            type: 'line',
            data: points,
            connectNulls: false,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 14,
            z: 5,
            clip: false,
            label: {
              show: true,
              formatter: (params) => (params.data && params.data.labelText) || ''
            },
            labelLayout: { hideOverlap: false },
            itemStyle: {
              color: peer.color,
              borderColor: '#fff',
              borderWidth: 3,
              shadowBlur: 16,
              shadowOffsetY: 6,
              shadowColor: 'rgba(15, 23, 42, 0.62)'
            },
            lineStyle: {
              color: peer.color,
              width: 8,
              shadowBlur: 22,
              shadowOffsetY: 12,
              shadowColor: 'rgba(15, 23, 42, 0.62)'
            },
            markPoint: {
              symbol: 'circle',
              symbolSize: 16,
              clip: false,
              z: 9,
              data: catchups.reduce((list, item) => {
                if (item.code !== peer.code || !Number.isFinite(item.y)) return list
                const x = dateIndex[item.date]
                if (x == null) return list
                list.push(this.catchupPoint(x + offsetAt(index, x), item.y))
                return list
              }, [])
            },
            emphasis: { disabled: true }
          }
        })
        this.chart.setOption({
          animationDuration: 180,
          backgroundColor: '#fff',
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const data = params.data || {}
              if (params.componentType === 'markPoint') return data.tip || ''
              const raw = params.value
              const value = Array.isArray(raw) ? Number(raw[1]) : Number(raw)
              if (!Number.isFinite(value)) return ''
              const x = Array.isArray(raw) ? raw[0] : null
              const date = x == null ? params.name : (dates[Math.round(x)] || '')
              const label = String(date).length >= 10 ? String(date).slice(5) : String(date || '')
              return `${params.seriesName}<br/>${label} · ${value.toFixed(1)}%`
            }
          },
          grid: { left: 52, right: 78, top: 36, bottom: 12 },
          xAxis: {
            type: 'value',
            min: this.axisSpan().min,
            max: this.axisSpan().max,
            interval: 1,
            axisLine: { lineStyle: { color: '#d5e3f2' } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false }
          },
          yAxis: {
            type: 'value',
            min: extent.min,
            max: extent.max,
            interval: 10,
            axisLabel: { color: '#000', fontSize: 12, fontWeight: 600, formatter: '{value}%' },
            splitLine: { show: false }
          },
          series: [kingSeries, ...fades, ...lines, {
            id: '__guide',
            type: 'line',
            data: [],
            silent: true,
            tooltip: { show: false },
            markLine: this.pickedDay < 0 ? { data: [] } : {
              symbol: 'none',
              silent: true,
              lineStyle: { color: '#409eff', type: 'dashed', width: 1 },
              label: { show: false },
              data: [{ xAxis: this.pickedDay }]
            }
          }]
        }, { notMerge: true })
        this.chart.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.kingdom-panel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.kingdom-body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.kingdom-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.kingdom-roster {
  flex: none;
  width: 196px;
  margin-left: 8px;
  padding: 4px 8px 8px;
  overflow: auto;
  border-left: 1px solid #e5e7eb;
  em { display: block; margin-bottom: 8px; color: #909399; font-style: normal; font-size: 12px; }
  &.is-adding { width: 248px; }
}
.kingdom-add { margin-bottom: 8px; }
.kingdom-add__input {
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
  input {
    flex: 1;
    min-width: 0;
    height: 28px;
    padding: 0 6px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #303133;
    font-size: 12px;
  }
  button {
    flex: none;
    height: 28px;
    padding: 0 8px;
    border: 1px solid #409eff;
    border-radius: 4px;
    background: #409eff;
    color: #fff;
    font-size: 12px;
    cursor: pointer;
  }
  button:disabled { opacity: 0.6; cursor: not-allowed; }
}
.kingdom-add__tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  button {
    flex: 1;
    height: 26px;
    padding: 0 4px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    color: #606266;
    font-size: 12px;
    cursor: pointer;
  }
  button.on { border-color: #409eff; color: #409eff; }
}
.kingdom-add__list {
  max-height: 220px;
  margin-bottom: 6px;
  overflow: auto;
}
.kingdom-add__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: 4px 2px;
  border: 0;
  background: transparent;
  color: #303133;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  i { flex: none; margin-left: 6px; color: #409eff; font-style: normal; font-size: 11px; }
}
.kingdom-add__row.is-picked { color: #409eff; font-weight: 700; }
.kingdom-add__row.is-on,
.kingdom-add__row:disabled {
  color: #c0c4cc;
  cursor: default;
  i { color: #c0c4cc; }
}
.kingdom-roster__row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  color: #303133;
  font-size: 13px;
  i {
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
  span { flex: 1; min-width: 0; }
  button {
    flex: none;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #909399;
    font-size: 16px;
    line-height: 16px;
    cursor: pointer;
  }
  button:hover { color: #f25c54; }
}
.kingdom-roster__save {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  padding: 4px 0;
  border: 1px solid #e6a23c;
  border-radius: 4px;
  background: #fff;
  color: #b88230;
  font-size: 12px;
  cursor: pointer;
}
.kingdom-roster__save:disabled {
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}
.kingdom-view {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.kingdom-tabs {
  position: absolute;
  z-index: 6;
  top: 8px;
  left: 64px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 72%;
  button {
    border: 1px solid #d7e6f5;
    background: #fff;
    color: #1f4e79;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
  button.is-on {
    border-color: #409eff;
    background: #ecf5ff;
  }
}
.kingdom-shift {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
}
.kingdom-chart { height: calc(100% - 68px); }
.kingdom-follow {
  position: absolute;
  top: 8px;
  z-index: 5;
  padding: 4px 8px;
  border: 1px solid #409eff;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  color: #1f4e79;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}
.kingdom-dates {
  position: relative;
  height: 68px;
}
.kingdom-dates__item {
  position: absolute;
  top: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #000;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  transform: translateX(-50%);
  b { font-weight: 600; }
  i { font-style: normal; font-weight: 700; }
  .is-hit { color: #0f766e; }
  .is-plus { color: #8B1515; }
  .is-soil { color: #C2410C; }
  .is-none { color: #6b7280; font-weight: 600; }
  .is-catchup { color: #7c3aed; font-size: 11px; font-weight: 700; }
}
.kingdom-bar {
  flex: none;
  height: 16px;
  overflow-x: scroll;
  overflow-y: hidden;
}
.kingdom-bar > div { height: 1px; }
</style>
